import React, { ComponentType } from 'react'
import { Image } from 'semantic-ui-react'
// Local
import {
  condition as conditionUtils,
  hasKnownAddressed,
  hasClosedActivity,
  hasKnown,
  hasPotential,
  withAddressedConditions,
  withClosedActivities
} from '../../common/conditionUtils'
import { EncounterConditionDisplay } from '../../authenticated/types'
import Checkmark from '../../icons/Checkmark'
import { shouldRenderReskin2025 } from '../../../constant'

const SafeImage = Image as unknown as ComponentType<any>

type Props = {
  condition?: EncounterConditionDisplay | undefined
  conditions?: EncounterConditionDisplay[] | undefined
}

function AddressedIcon(
  props: Props
) {
  const {
    condition = undefined,
    conditions = undefined
  } = props
  const greenCheckMark = () => shouldRenderReskin2025
    ? (
      <Checkmark />
    )
    : (
      <SafeImage
        src="/asset/image/checkmark-green.png"
        className="addressed-icon"
      />
    )

  /**
 * Apply check mark to stand alone known condition.
 * Known addressed conditions are green
 */
  const applyStandAloneKnownCheckMark = (
    conditionProp: EncounterConditionDisplay
  ) => {
    if (conditionUtils.isKnownAddressed(conditionProp)) {
      return greenCheckMark()
    }
    return null
  }

  /**
   * Apply check mark to stand alone potential condition
   * Potential addressed conditions get none
   */
  const applyStandAlonePotentialCheckMark = () => null

  /**
   * Apply check mark to stand alone closed activity condition.
   * First check for check mark guidance. If no guidance, closed activity conditions are green
   */
  const applyStandAloneActivityCheckMark = () => greenCheckMark()

  /*
   * Stand alone condition rendering logic
   */
  const renderStandAloneCheckMark = (
    conditionProp: EncounterConditionDisplay
  ) => {
    if (conditionUtils.isKnown(conditionProp)) {
      return applyStandAloneKnownCheckMark(conditionProp)
    } if (conditionUtils.isPotential(conditionProp)) {
      return applyStandAlonePotentialCheckMark()
    } if (conditionUtils.isClosedActivity(conditionProp)) {
      return applyStandAloneActivityCheckMark()
    }
    return null
  }

  /**
   * Apply check mark to group of known conditions.
   */
  const applyGroupKnownCheckMark = (
    conditionsProp: EncounterConditionDisplay[]
  ) => {
    if (hasKnownAddressed(conditionsProp)) {
      return greenCheckMark()
    }
    return null
  }

  /**
   * Apply check mark to group of potential conditions.
   * IPotential addressed conditions are none
   */
  const applyGroupPotentialCheckMark = () => null

  /**
   * Apply check mark to group of activity conditions.
   * First check for check mark guidance. If no guidance, closed activity conditions are green
   */
  const applyGroupActivityCheckMark = () => greenCheckMark()

  /*
   * Condition group rendering logic
   */
  const renderGroupCheckMark = (
    conditionsProp: EncounterConditionDisplay[]
  ) => {
    if (hasKnown(conditionsProp)) {
      return applyGroupKnownCheckMark(conditionsProp)
    } if (hasPotential(conditionsProp)) {
      return applyGroupPotentialCheckMark()
    } if (hasClosedActivity(conditionsProp)) {
      return applyGroupActivityCheckMark()
    }
    return null

  }

  /**
   * Return true if props provides a stand alone condition and that condition
   * is addressed or is a closed activity
   */
  const withCondition = () => condition && (
    conditionUtils.isConditionAddressed(condition) || conditionUtils.isClosedActivity(condition)
  )

  /**
   * Return true if props provides a condition group and that condition
   * group contains addressed conditions or is a closed activities
   */
  const withConditions = () => conditions && (
    withAddressedConditions(conditions).length > 0
    || withClosedActivities(conditions).length > 0
  )

  if (withCondition() && !!condition) {
    return renderStandAloneCheckMark(condition)
  }
  if (withConditions() && !!conditions) {
    return renderGroupCheckMark(conditions)
  }
  return null
}

export default AddressedIcon
